/**
 * Wake-on-screen: when a screen opens, send the browser's own CORS pre-flight to the callables
 * the screen may use, so a sleeping (cold) function starts waking BEFORE the user presses the
 * button. Measured 2026-10-09: a cold pre-flight took 18-46 s; the same URL right after a knock
 * took ~0.17 s.
 *
 * The knock is a credential-less cross-origin OPTIONS with no body and no custom headers. Every
 * callable's CORS layer (the `cors` package inside firebase-functions' onCall) answers ANY OPTIONS
 * request itself with 204 and never calls the handler, so the knock wakes the instance and the
 * function logs no ERROR, whatever the browser does: Chrome/Android/desktop send a pre-flight
 * (OPTIONS) and stop there; iOS Safari was measured sending the request itself (2026-10-12).
 * WHY NOT PUT (the earlier knock): iOS Safari sent the PUT with no pre-flight, it reached the
 * handler, and onCall logged 'Invalid request, unable to process' at ERROR: all 379 such lines
 * that day came from iPhones. (A no-cors POST was rejected earlier for the same reason: an ERROR
 * line per knock.) An OPTIONS-only knock is what already woke cold services on desktop/Android:
 * 388 of 2,000 sampled took >= 8 s (a cold start absorbed by the knock).
 *
 * Framework-neutral on purpose: every app (office, Technician, Sales, Time, Shop) shares this
 * file; the React wrapper lives in ../react/useWakeCallables.ts.
 */

export const WAKE_THROTTLE_MS = 10 * 60 * 1000;
export const WAKE_STAGGER_MS = 150;
export const WAKE_MAX_NAMES = 12;
const STORE_KEY = 'oh.wake.v1';
/** A callable export name: letters, digits, underscore. Anything else never reaches a URL. */
const CALLABLE_NAME = /^[A-Za-z][A-Za-z0-9_]*$/;

export interface WakeStore {
  get(name: string): number | undefined;
  set(name: string, at: number): void;
  clear(name: string): void;
}

export interface WakeConfig {
  /** Same URL the Firebase SDK uses for the real call, so the same service is warmed. */
  readonly urlFor: (name: string) => string;
  /** Global kill switch AND per-screen flag, evaluated on every call. Default: on. */
  readonly enabled?: () => boolean;
  /** Never knock offline. Default: navigator.onLine. */
  readonly isOnline?: () => boolean;
  readonly fetchImpl?: typeof fetch;
  readonly now?: () => number;
  readonly store?: WakeStore;
  readonly throttleMs?: number;
  readonly staggerMs?: number;
  readonly maxNames?: number;
}

export interface WakeHandle {
  /** Callables scheduled by THIS call (after throttle, validation and the cap). */
  readonly scheduled: readonly string[];
  /** Stop knocks not yet sent and free their throttle slot. */
  cancel(): void;
}

export function createMemoryWakeStore(): WakeStore {
  const m = new Map<string, number>();
  return {
    get: (n) => m.get(n),
    set: (n, at) => { m.set(n, at); },
    clear: (n) => { m.delete(n); },
  };
}

/**
 * localStorage-backed (shared across tabs); falls back to memory when storage is unavailable.
 * One key PER callable, so two tabs opening at once cannot overwrite each other's entries.
 */
export function createBrowserWakeStore(): WakeStore {
  const memory = createMemoryWakeStore();
  const key = (n: string) => `${STORE_KEY}.${n}`;
  return {
    get: (n) => {
      try {
        const raw = typeof localStorage === 'undefined' ? null : localStorage.getItem(key(n));
        const v = raw === null ? NaN : Number(raw);
        if (Number.isFinite(v)) return v;
      } catch { /* storage blocked: fall through to memory */ }
      return memory.get(n);
    },
    set: (n, at) => {
      memory.set(n, at);
      try { localStorage.setItem(key(n), String(at)); } catch { /* memory above still throttles this session */ }
    },
    clear: (n) => {
      memory.clear(n);
      try { localStorage.removeItem(key(n)); } catch { /* nothing to clear */ }
    },
  };
}

/** One store per page, so the memory fallback survives across calls when localStorage is blocked. */
let sharedBrowserStore: WakeStore | undefined;
function browserStore(): WakeStore {
  return (sharedBrowserStore ??= createBrowserWakeStore());
}

const NOTHING: WakeHandle = { scheduled: [], cancel: () => {} };

function safely<T>(fn: () => T, fallback: T): T {
  try { return fn(); } catch { return fallback; }
}

/**
 * DECISIONS, so the next reader does not "fix" them:
 *  - A knock is EXPECTED to reject (Chrome blocks the OPTIONS itself after its pre-flight), so a rejection
 *    cannot be told from a lost connection and a knock always uses up its 10-minute slot. A flaky
 *    link therefore skips a knock rather than retrying in a loop.
 *  - No AbortSignal: a pre-flight cancelled before the cold server answers might not finish waking
 *    it. In-flight knocks cannot pile up -- the throttle allows one per callable per 10 minutes.
 */
function send(fetchImpl: typeof fetch, url: string): void {
  try {
    // OPTIONS, no body, no headers, no credentials, no Referer: only the CORS layer may answer it (see file header). referrerPolicy keeps a
    // page URL that carries a secret (the invitation token in /invite/:token) out of the request the browser sends.
    fetchImpl(url, { method: 'OPTIONS', mode: 'cors', credentials: 'omit', cache: 'no-store', referrerPolicy: 'no-referrer' })
      .then(() => undefined, () => undefined);
  } catch { /* a knock is best effort; never surface */ }
}

/**
 * Milliseconds since this browser last scheduled a knock for `name` (any tab: the store is shared through localStorage), or null when it
 * never did / the name is not a callable name / the store cannot be read. For the "why was this call cold" report only: it never knocks.
 * The mark is written when a knock is SCHEDULED, so it can run a few seconds ahead of the request that went out (staggered 150 ms apiece).
 */
export function msSinceLastWake(name: string, now: () => number = Date.now, store: WakeStore = browserStore()): number | null {
  if (!CALLABLE_NAME.test(name)) return null;
  const last = safely(() => store.get(name), undefined);
  return last === undefined ? null : Math.max(0, now() - last);
}

export function wakeCallables(names: readonly string[], config: WakeConfig): WakeHandle {
  if (!safely(() => (config.enabled ? config.enabled() : true), false)) return NOTHING;
  if (!safely(() => (config.isOnline ? config.isOnline() : (typeof navigator === 'undefined' || navigator.onLine !== false)), false)) return NOTHING;

  const fetchImpl = config.fetchImpl ?? (typeof fetch === 'undefined' ? undefined : fetch.bind(globalThis));
  if (!fetchImpl) return NOTHING;
  const now = config.now ?? Date.now;
  const store = config.store ?? browserStore();
  const throttleMs = config.throttleMs ?? WAKE_THROTTLE_MS;
  const staggerMs = config.staggerMs ?? WAKE_STAGGER_MS;
  const maxNames = config.maxNames ?? WAKE_MAX_NAMES;

  const unique = Array.from(new Set(names)).filter((n) => CALLABLE_NAME.test(n)).slice(0, maxNames);
  const due = unique.filter((n) => {
    const last = safely(() => store.get(n), undefined);
    return last === undefined || now() - last >= throttleMs;
  });
  if (due.length === 0) return NOTHING;

  // Mark at scheduling time so a quick remount cannot double-knock; cancel() unmarks unsent ones.
  due.forEach((n) => safely(() => store.set(n, now()), undefined));

  const sent = new Set<string>();
  const timers: ReturnType<typeof setTimeout>[] = [];
  due.forEach((name, i) => {
    timers.push(setTimeout(() => {
      sent.add(name);
      safely(() => send(fetchImpl, config.urlFor(name)), undefined);
    }, i * staggerMs));
  });

  return {
    scheduled: due,
    cancel: () => {
      timers.forEach(clearTimeout);
      due.filter((n) => !sent.has(n)).forEach((n) => safely(() => store.clear(n), undefined));
    },
  };
}
